import * as THREE from 'three';
import {
  MODEL_CATALOG,
  createEnvironmentModel,
  type EnvironmentModelId,
} from './catalog';

export type RuntimeModelBiome = 'lanternwood' | 'blueIce' | 'timberline';
export type RuntimeCollisionKind = 'tree' | 'rock' | 'ice';

export interface RuntimeCollisionCircle {
  /** Model-local cross-slope offset. */
  readonly x: number;
  /** Model-local downhill offset. */
  readonly z: number;
  readonly radius: number;
}

export interface RuntimeModelDefinition {
  readonly id: EnvironmentModelId;
  readonly biome: RuntimeModelBiome;
  /** Per-placement-attempt probability at full biome weight. */
  readonly density: number;
  readonly minScale: number;
  readonly maxScale: number;
  /** Model-space distance to settle below the sampled snow surface. */
  readonly sink: number;
  readonly collisionKind: RuntimeCollisionKind;
  readonly collisionCircles: readonly RuntimeCollisionCircle[];
  /** Large, deliberately uncommon feature that should read as a landmark. */
  readonly landmark?: boolean;
}

/**
 * Immutable placement streams keep existing populations stable when catalog
 * entries are inserted, removed, or regrouped for the field guide.
 */
export const RUNTIME_MODEL_PLACEMENT_SEEDS = {
  'ranger-hut': 5_003,
  'birch-cluster': 6_012,
  'fallen-log': 7_021,
  snowman: 8_030,
  'snowshoe-hare': 9_039,
  'ice-arch': 10_048,
  'crystal-fan': 11_057,
  'frozen-icefall': 12_066,
  krummholz: 13_075,
  'ribbon-cairn': 14_084,
  'avalanche-fence': 15_093,
  'weather-mast': 16_102,
  'snow-diving-fox': 17_111,
  'icebound-survey-sled': 18_120,
  'raven-cache': 19_129,
  'mitten-skidding-ermine': 20_138,
  'powder-bathing-ptarmigan-pair': 21_147,
} as const satisfies Record<EnvironmentModelId, number>;

/**
 * Runtime placement tuning for every gallery model.
 *
 * Collision circles are expressed in the centered, grounded model coordinates
 * produced by the catalog factories. World placement scales and rotates them
 * along with the baked geometry.
 */
export const RUNTIME_MODEL_CATALOG = [
  {
    id: 'ranger-hut',
    biome: 'lanternwood',
    density: 0.004,
    minScale: 0.78,
    maxScale: 1.04,
    sink: 0.12,
    collisionKind: 'rock',
    collisionCircles: [
      { x: -2.15, z: 0, radius: 1.85 },
      { x: 1.65, z: 0, radius: 1.85 },
    ],
    landmark: true,
  },
  {
    id: 'birch-cluster',
    biome: 'lanternwood',
    density: 0.032,
    minScale: 0.72,
    maxScale: 1.18,
    sink: 0.18,
    collisionKind: 'tree',
    collisionCircles: [
      { x: -1.1, z: 0.22, radius: 0.44 },
      { x: -0.2, z: -0.42, radius: 0.46 },
      { x: 0.72, z: 0.48, radius: 0.43 },
      { x: 1.45, z: -0.28, radius: 0.36 },
    ],
  },
  {
    id: 'fallen-log',
    biome: 'lanternwood',
    density: 0.018,
    minScale: 0.76,
    maxScale: 1.16,
    sink: 0.2,
    collisionKind: 'tree',
    collisionCircles: [
      { x: -2.25, z: 0, radius: 0.72 },
      { x: 0, z: 0.08, radius: 0.72 },
      { x: 2.25, z: 0.16, radius: 0.72 },
    ],
  },
  {
    id: 'snowman',
    biome: 'lanternwood',
    density: 0.011,
    minScale: 0.72,
    maxScale: 1.08,
    sink: 0.12,
    collisionKind: 'rock',
    collisionCircles: [{ x: 0, z: 0, radius: 0.92 }],
  },
  {
    id: 'snowshoe-hare',
    biome: 'lanternwood',
    density: 0.018,
    minScale: 0.68,
    maxScale: 0.98,
    sink: 0.08,
    collisionKind: 'rock',
    collisionCircles: [{ x: 0.12, z: 0, radius: 0.48 }],
  },
  {
    id: 'ice-arch',
    biome: 'blueIce',
    density: 0.008,
    minScale: 0.82,
    maxScale: 1.12,
    sink: 0.22,
    collisionKind: 'ice',
    // Keep the center open: each foot collides independently so the skier can
    // use the arch as a gate instead of hitting one broad invisible obstacle.
    collisionCircles: [
      { x: -4.18, z: 0, radius: 1.42 },
      { x: 4.18, z: 0, radius: 1.42 },
    ],
    landmark: true,
  },
  {
    id: 'crystal-fan',
    biome: 'blueIce',
    density: 0.055,
    minScale: 0.68,
    maxScale: 1.22,
    sink: 0.2,
    collisionKind: 'ice',
    collisionCircles: [
      { x: -0.92, z: 0, radius: 1.05 },
      { x: 0.9, z: 0.06, radius: 1.05 },
    ],
  },
  {
    id: 'frozen-icefall',
    biome: 'blueIce',
    density: 0.006,
    minScale: 0.74,
    maxScale: 1.06,
    sink: 0.28,
    collisionKind: 'ice',
    collisionCircles: [
      { x: -2.8, z: 0.25, radius: 1.62 },
      { x: 0, z: 0.25, radius: 1.62 },
      { x: 2.8, z: 0.25, radius: 1.62 },
    ],
    landmark: true,
  },
  {
    id: 'krummholz',
    biome: 'timberline',
    density: 0.045,
    minScale: 0.72,
    maxScale: 1.2,
    sink: 0.16,
    collisionKind: 'tree',
    collisionCircles: [
      { x: -0.78, z: 0, radius: 0.62 },
      { x: 0.7, z: 0, radius: 0.58 },
      { x: 1.85, z: -0.08, radius: 0.48 },
    ],
  },
  {
    id: 'ribbon-cairn',
    biome: 'timberline',
    density: 0.022,
    minScale: 0.74,
    maxScale: 1.18,
    sink: 0.14,
    collisionKind: 'rock',
    collisionCircles: [{ x: 0, z: 0, radius: 0.9 }],
  },
  {
    id: 'avalanche-fence',
    biome: 'timberline',
    density: 0.006,
    minScale: 0.78,
    maxScale: 1.08,
    sink: 0.2,
    collisionKind: 'tree',
    collisionCircles: [
      { x: -3.45, z: 0, radius: 1.05 },
      { x: -1.15, z: 0, radius: 1.05 },
      { x: 1.15, z: 0, radius: 1.05 },
      { x: 3.45, z: 0, radius: 1.05 },
    ],
    landmark: true,
  },
  {
    id: 'weather-mast',
    biome: 'timberline',
    density: 0.008,
    minScale: 0.8,
    maxScale: 1.12,
    sink: 0.16,
    collisionKind: 'rock',
    collisionCircles: [{ x: 0, z: 0, radius: 1.42 }],
    landmark: true,
  },
  {
    id: 'snow-diving-fox',
    biome: 'lanternwood',
    density: 0.014,
    minScale: 0.72,
    maxScale: 1.04,
    sink: 0.1,
    collisionKind: 'tree',
    collisionCircles: [
      { x: -1.65, z: 0, radius: 0.72 },
      { x: 0, z: 0, radius: 0.76 },
      { x: 1.65, z: 0, radius: 0.62 },
    ],
  },
  {
    id: 'icebound-survey-sled',
    biome: 'blueIce',
    density: 0.0065,
    minScale: 0.76,
    maxScale: 1.06,
    sink: 0.18,
    collisionKind: 'ice',
    collisionCircles: [
      { x: -2.55, z: 0, radius: 1.08 },
      { x: -0.65, z: 0, radius: 1.14 },
      { x: 1.45, z: 0, radius: 1.18 },
      { x: 3.15, z: 0, radius: 0.72 },
    ],
    landmark: true,
  },
  {
    id: 'raven-cache',
    biome: 'timberline',
    density: 0.015,
    minScale: 0.76,
    maxScale: 1.08,
    sink: 0.12,
    collisionKind: 'rock',
    collisionCircles: [
      { x: -0.48, z: 0, radius: 0.92 },
      { x: 0.72, z: 0, radius: 0.72 },
    ],
  },
  {
    id: 'mitten-skidding-ermine',
    biome: 'blueIce',
    density: 0.014,
    minScale: 0.82,
    maxScale: 1.18,
    sink: 0.07,
    collisionKind: 'ice',
    collisionCircles: [
      { x: -0.9, z: 0, radius: 0.54 },
      { x: 0.48, z: 0.18, radius: 0.52 },
    ],
  },
  {
    id: 'powder-bathing-ptarmigan-pair',
    biome: 'timberline',
    density: 0.012,
    minScale: 0.92,
    maxScale: 1.22,
    sink: 0.1,
    collisionKind: 'rock',
    collisionCircles: [
      { x: -0.72, z: 0.04, radius: 0.5 },
      { x: 0.72, z: -0.04, radius: 0.5 },
    ],
  },
] as const satisfies readonly RuntimeModelDefinition[];

function materialColor(material: THREE.Material | undefined): THREE.Color {
  const color = (material as (THREE.Material & { color?: THREE.Color }) | undefined)?.color;
  return color?.isColor ? color : new THREE.Color(0xffffff);
}

function finiteColorComponent(value: number): number {
  return Number.isFinite(value) ? value : 1;
}

function appendBakedMesh(
  mesh: THREE.Mesh,
  positions: number[],
  normals: number[],
  colors: number[],
): void {
  const source = mesh.geometry;
  if (!(source instanceof THREE.BufferGeometry)) return;
  const sourcePosition = source.getAttribute('position');
  if (!sourcePosition || sourcePosition.itemSize < 3 || sourcePosition.count === 0) return;

  const geometry = source.index ? source.toNonIndexed() : source.clone();
  try {
    if (!geometry.getAttribute('normal')) geometry.computeVertexNormals();
    geometry.applyMatrix4(mesh.matrixWorld);

    const position = geometry.getAttribute('position');
    const normal = geometry.getAttribute('normal');
    if (!position || !normal) return;

    const sourceColor = geometry.getAttribute('color');
    const materials = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
    const materialIndices = new Uint16Array(position.count);

    // Geometry groups use index offsets for indexed sources. toNonIndexed()
    // preserves those offsets as vertex offsets, making this assignment valid
    // for either source representation.
    for (const group of geometry.groups) {
      const start = Math.max(0, Math.min(position.count, Math.floor(group.start)));
      const end = Math.max(start, Math.min(position.count, Math.floor(group.start + group.count)));
      const materialIndex = Math.max(0, Math.min(materials.length - 1, group.materialIndex ?? 0));
      materialIndices.fill(materialIndex, start, end);
    }

    for (let index = 0; index < position.count; index += 1) {
      const x = position.getX(index);
      const y = position.getY(index);
      const z = position.getZ(index);
      const nx = normal.getX(index);
      const ny = normal.getY(index);
      const nz = normal.getZ(index);
      if (![x, y, z, nx, ny, nz].every(Number.isFinite)) {
        throw new Error(`Model mesh "${mesh.name}" produced non-finite baked geometry.`);
      }

      positions.push(x, y, z);
      normals.push(nx, ny, nz);

      const base = materialColor(materials[materialIndices[index] ?? 0]);
      const vertexRed = sourceColor && index < sourceColor.count ? sourceColor.getX(index) : 1;
      const vertexGreen = sourceColor && index < sourceColor.count ? sourceColor.getY(index) : 1;
      const vertexBlue = sourceColor && index < sourceColor.count ? sourceColor.getZ(index) : 1;
      colors.push(
        finiteColorComponent(base.r * vertexRed),
        finiteColorComponent(base.g * vertexGreen),
        finiteColorComponent(base.b * vertexBlue),
      );
    }
  } finally {
    geometry.dispose();
  }
}

function disposeOwnedSourceGeometry(root: THREE.Object3D): void {
  const geometries = new Set<THREE.BufferGeometry>();
  root.traverse((object) => {
    if (object instanceof THREE.Mesh && object.geometry instanceof THREE.BufferGeometry) {
      geometries.add(object.geometry);
    }
  });
  for (const geometry of geometries) geometry.dispose();
  // Catalog materials are module-level shared resources. They intentionally
  // remain alive for the gallery and for future model factory calls.
}

/**
 * Flattens one catalog Group into a grounded, non-indexed geometry containing
 * only the attributes needed by the runtime InstancedMesh material.
 */
export function bakeEnvironmentModelGeometry(id: EnvironmentModelId): THREE.BufferGeometry {
  const model = createEnvironmentModel(id);
  const positions: number[] = [];
  const normals: number[] = [];
  const colors: number[] = [];

  try {
    model.updateMatrixWorld(true);
    model.traverse((object) => {
      if (object instanceof THREE.Mesh && object.visible) {
        appendBakedMesh(object, positions, normals, colors);
      }
    });
  } finally {
    disposeOwnedSourceGeometry(model);
  }

  if (positions.length === 0) throw new Error(`Environment model "${id}" has no bakeable geometry.`);
  if (positions.length !== normals.length || positions.length !== colors.length) {
    throw new Error(`Environment model "${id}" produced inconsistent baked attributes.`);
  }

  const geometry = new THREE.BufferGeometry();
  geometry.name = `${id}-runtime-geometry`;
  geometry.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
  geometry.setAttribute('normal', new THREE.Float32BufferAttribute(normals, 3));
  geometry.setAttribute('color', new THREE.Float32BufferAttribute(colors, 3));
  geometry.computeBoundingBox();

  const minimumY = geometry.boundingBox?.min.y;
  if (minimumY === undefined || !Number.isFinite(minimumY)) {
    geometry.dispose();
    throw new Error(`Environment model "${id}" produced invalid bounds.`);
  }
  if (minimumY !== 0) geometry.translate(0, -minimumY, 0);
  geometry.computeBoundingBox();
  geometry.computeBoundingSphere();
  return geometry;
}

// A compile-time reminder that the runtime table is intended to shadow the
// source catalog; the executable coverage assertion lives in the unit test.
void MODEL_CATALOG;
